import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/outline";
import {
  Children,
  DetailedHTMLProps,
  HTMLAttributes,
  PropsWithChildren,
  cloneElement,
  createContext,
  forwardRef,
  useContext,
} from "react";

import { classNames } from "@helpers/css";

export type TableSortDir = "asc" | "desc";

export enum TableTheme {
  default = "default",
  whiteHeader = "whiteHeader",
}
export enum TableSticky {
  none = "none",
  onlyTop = "onlyTop",
  onlyLeft = "onlyLeft",
  topAndLeft = "topAndLeft",
}

export const TableThemeContext = createContext(TableTheme.default);
export const TableStickyContext = createContext(TableSticky.none);

export type TableContainerProps = DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  scroll?: boolean;
  theme?: TableTheme;
};

export const TableContainer = forwardRef<
  HTMLDivElement,
  PropsWithChildren<TableContainerProps>
>(
  (
    { children, scroll, className, theme = TableTheme.default, ...props },
    ref
  ) => (
    <div
      ref={ref}
      className={classNames(
        "min-w-full border",
        theme === TableTheme.default && "rounded-md",
        theme === TableTheme.whiteHeader && "rounded-lg",
        scroll ? "overflow-scroll" : "overflow-hidden",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
);

export type TableProps = {
  theme?: TableTheme;
  sticky?: TableSticky;
} & HTMLAttributes<HTMLTableElement>;

export const Table = ({
  children,
  theme = TableTheme.default,
  sticky = TableSticky.none,
  className,
  ...props
}: PropsWithChildren<TableProps>) => {
  return (
    <table
      className={classNames(
        "bg-white min-w-full divide-y divide-gray-200",
        className
      )}
      {...props}
    >
      <TableStickyContext.Provider value={sticky}>
        <TableThemeContext.Provider value={theme}>
          {children}
        </TableThemeContext.Provider>
      </TableStickyContext.Provider>
    </table>
  );
};

export const TableHeadCell = ({
  className = "",
  cellClassName,
  width = "",
  widthPx,
  children = "",
  roundedLeft = false,
  roundedRight = false,
  sorted,
  style,
  isFirst,
  onClick,
  ...props
}: PropsWithChildren<{
  className?: string;
  cellClassName?: string;
  width?: string;
  widthPx?: number;
  roundedLeft?: boolean;
  roundedRight?: boolean;
  sorted?: TableSortDir | null;
  style?: React.CSSProperties;
  onClick?: () => void;
  isFirst?: boolean;
}>) => {
  const theme = useContext(TableThemeContext);
  const sticky = useContext(TableStickyContext);
  return (
    <th
      scope="col"
      className={classNames(
        "text-left",
        theme === TableTheme.default &&
          "text-xs font-medium text-gray-500 bg-gray-50 py-3 px-5",
        theme === TableTheme.whiteHeader &&
          "text-xs font-medium text-gray-500 py-4 px-4",
        width && `w-${width}`,
        roundedLeft && "rounded-tl-md",
        roundedRight && "rounded-tr-md",
        onClick && "cursor-pointer hover:underline",
        sticky === TableSticky.onlyLeft && isFirst && "sticky left-0 z-10",
        sticky === TableSticky.onlyTop && "sticky top-0 z-10",
        sticky === TableSticky.topAndLeft &&
          isFirst &&
          "sticky top-0 left-0 z-20",
        sticky === TableSticky.topAndLeft && !isFirst && "sticky top-0 z-10",
        cellClassName
      )}
      style={{
        width: widthPx !== undefined ? `${widthPx}px` : undefined,
        boxShadow:
          (sticky === TableSticky.onlyLeft ||
            sticky === TableSticky.topAndLeft) &&
          isFirst
            ? "inset -8px 0 8px -8px rgba(0,0,0,0.08)"
            : undefined,
        ...style,
      }}
      {...props}
      onClick={onClick}
    >
      <div
        className={classNames(
          className,
          "flex items-center justify-start gap-2"
        )}
      >
        <div className="flex-auto">{children}</div>
        {sorted === "asc" && (
          <div>
            <ArrowUpIcon className="h-3 w-3" />
          </div>
        )}
        {sorted === "desc" && (
          <div>
            <ArrowDownIcon className="h-3 w-3" />
          </div>
        )}
      </div>
    </th>
  );
};

export const TableHeadRow = ({
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLTableSectionElement>>) => {
  const childrenArray = Children.toArray(children);
  return (
    <thead {...props}>
      <tr>
        {childrenArray.map((child, index) =>
          cloneElement(child as React.ReactElement, {
            isFirst: index === 0,
          })
        )}
      </tr>
    </thead>
  );
};

export const TableBody = ({ children }: PropsWithChildren) => (
  <tbody>{children}</tbody>
);

export const TableBodyRow = ({
  children,
  className,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLTableRowElement>>) => {
  const childrenArray = Children.toArray(children);
  return (
    <tr className={classNames("border-t", className)} {...props}>
      {childrenArray.map((child, index) =>
        cloneElement(child as React.ReactElement, {
          isFirst: index === 0,
        })
      )}
    </tr>
  );
};

export const TableBodyCell = forwardRef<
  HTMLTableCellElement,
  PropsWithChildren<
    HTMLAttributes<HTMLTableCellElement> & {
      colSpan?: number;
      isFirst?: boolean;
    }
  >
>(({ children, className = "", colSpan, onClick, isFirst, ...props }, ref) => {
  const theme = useContext(TableThemeContext);
  const sticky = useContext(TableStickyContext);
  return (
    <td
      ref={ref}
      className={classNames(
        "min-w-0",
        "text-sm text-gray-500 bg-white",
        theme === TableTheme.default && "px-5 py-3",
        theme === TableTheme.whiteHeader && "px-4 py-4",
        (sticky === TableSticky.onlyLeft ||
          sticky === TableSticky.topAndLeft) &&
          isFirst &&
          "sticky left-0 z-1",
        className
      )}
      style={{
        boxShadow:
          (sticky === TableSticky.onlyLeft ||
            sticky === TableSticky.topAndLeft) &&
          isFirst
            ? "inset -8px 0 8px -8px rgba(0,0,0,0.08)"
            : undefined,
      }}
      colSpan={colSpan}
      onClick={onClick}
      {...props}
    >
      {children}
    </td>
  );
});

export const TableFooter = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <div
    className={classNames("px-5 pt-3 pb-1 text-sm text-gray-700", className)}
  >
    {children}
  </div>
);

export default Table;
